import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { tenantEndpoints } from './endpoints.js';
import {
  attachmentCredential,
  moduleOption,
  tenantDetail,
  type AttachmentCredential,
  type CityOption,
  type CountryOption,
  type ModuleOption,
  type StateOption,
  type TenantDetail,
} from './schemas.js';

/**
 * Reads for the Tenants module.
 *
 * Lists are handled by `ResourceListPage` so the list code is shared; only
 * detail and the reference-data cascade live here.
 *
 * The three reference queries read from reference-service, which answers the
 * option shape `{ value, label, meta }`. The tenant write path takes surrogate
 * ids, so each query maps `meta.id` back into the `{ id, code, name }` shape
 * the form already holds.
 */

const optionSchema = z.object({
  value: z.string(),
  label: z.string(),
  meta: z.record(z.string(), z.string().nullable()).optional(),
});

const countryOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    id: option.meta?.id ?? '',
    code: option.value,
    name: option.label,
    phonePrefix: option.meta?.phonePrefix ?? null,
  })),
);

const stateOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    id: option.meta?.id ?? '',
    code: option.value,
    name: option.label,
  })),
);

const cityOptions = z.array(optionSchema).transform((options) =>
  options.map((option) => ({
    id: option.meta?.id ?? '',
    code: option.value,
    name: option.label,
  })),
);

export function useTenant(tenantId: string): UseQueryResult<TenantDetail> {
  return useApiQuery({
    queryKey: queryKeys.tenants.detail(tenantId),
    path: tenantEndpoints.detail(tenantId),
    schema: tenantDetail,
    enabled: tenantId !== '',
  });
}

export function useAttachmentCredentials(tenantId: string): UseQueryResult<AttachmentCredential> {
  return useApiQuery({
    queryKey: queryKeys.tenants.attachmentCredentials(tenantId),
    path: tenantEndpoints.attachmentCredentials(tenantId),
    schema: attachmentCredential,
    enabled: tenantId !== '',
  });
}

/**
 * The sellable modules, as the platform currently ships them.
 *
 * Served rather than compiled in, so a module released after this build still
 * appears in the picker.
 */
export function useModuleCatalogue(): UseQueryResult<readonly ModuleOption[]> {
  return useApiQuery({
    queryKey: queryKeys.tenants.modules,
    path: tenantEndpoints.modules,
    schema: z.array(moduleOption),
  });
}

export function useCountries(q = ''): UseQueryResult<readonly CountryOption[]> {
  return useApiQuery({
    queryKey: [...queryKeys.reference.countries, q],
    path: tenantEndpoints.countries,
    query: { q },
    schema: countryOptions,
  });
}

/**
 * The states of one country.
 *
 * The form holds a `countryId` (surrogate), but the options endpoint takes a
 * `countryCode`. The countries list the form already holds carries both, so
 * the code is resolved from it rather than by a second round trip.
 */
export function useStates(
  countryId: string,
  q = '',
  countries?: readonly CountryOption[],
): UseQueryResult<readonly StateOption[]> {
  const countryCode = countries?.find((entry) => entry.id === countryId)?.code ?? '';
  return useApiQuery({
    queryKey: [...queryKeys.reference.states(countryCode || countryId), q],
    path: tenantEndpoints.states(countryCode),
    query: { q },
    schema: stateOptions,
    enabled: countryId !== '' && countryCode !== '',
  });
}

export function useCities(
  countryId: string,
  stateId: string,
  q = '',
  countries?: readonly CountryOption[],
  states?: readonly StateOption[],
): UseQueryResult<readonly CityOption[]> {
  const countryCode = countries?.find((entry) => entry.id === countryId)?.code ?? '';
  const stateCode = states?.find((entry) => entry.id === stateId)?.code ?? '';
  return useApiQuery({
    queryKey: [...queryKeys.reference.cities(countryCode, stateCode), q],
    path: tenantEndpoints.cities(countryCode, stateCode),
    query: { q },
    schema: cityOptions,
    enabled: countryId !== '' && stateId !== '' && countryCode !== '' && stateCode !== '',
  });
}
